import { QueryClient, UseQueryOptions, useMutation, useQuery } from "@tanstack/react-query";
import { getApiKey } from "../services/apiKeyEndpoint";
import { readAllAutomations, readAutomationLogs, readSignalAutomations } from "../services/automation/automationEndpoints";
import { getAccountById } from "../services/companyEndpoint";
import { readAllMetrics } from "../services/metric/metricEndpoint";
import { readAllPlaybooks } from "../services/playbook/playbookEndpoints";
import { readWorkspacePreference } from "../services/preference/preferenceEndpoints";
import { getContactById, getContactsByAccountId } from "../services/userContactsEndpoint";
import { currentUser } from "../services/usersEndpoint";
import { getViews } from "../services/view/viewEndpoints";
import { getWorkspace, getWorkspaceTraitsAndMetrics } from "../services/workspace/workspaceEndpoints";
import { allWorkspaceMembers } from "../services/workspaceMemberEndpoint";
import arrayToObject from "../utils/helpers";
import notify from "../utils/notify";
import { readSignalRuns } from "../services/inbox/inboxEndpoints";
import {
  getAllIntegration, getIntegrationEvents, getSlackChannels, getSlackIntegration, getSlackUsers,
} from "../services/integrationEndpoint";
import { ExportResponse, ListExportsResponse } from "../services/exports/exportsEndpoints.interface";
import { createExport, downloadExport, getExport, listExports } from "../services/exports/exportsEndpoints";

export const queryClient = new QueryClient();

export const useCurrentUser = ({ ...queryOptions }: UseQueryOptions) => useQuery({
  queryKey: ["user"],
  queryFn: async () => {
    const response = await currentUser();
    if (!(response.status === 200)) {
      notify({
        heading: "Error",
        messageList: ["Failed to fetch user, kindly try again"],
        type: "failure",
      });
      // eslint-disable-next-line @typescript-eslint/no-throw-literal
    }
    return response.data;
  },
  staleTime: 1000 * 60 * 60 * 24,
  ...queryOptions,
});

export const useApiKey = ({ workspaceId, ...queryOptions }:
{ workspaceId?: string } & UseQueryOptions) => useQuery({
  queryKey: ["apiKey", workspaceId],
  queryFn: () => getApiKey({
    workspace_id: workspaceId,
  }),
  select: (data: any) => data?.data,
  staleTime: 1000 * 60 * 60 * 24,
  ...queryOptions,
});

export const useGetEvents = ({
  workspaceId, integration_name, token, ...queryOptions
}:
{ workspaceId: string, integration_name: string, token: string } & UseQueryOptions) => useQuery({
  queryKey: ["getEvents", workspaceId, integration_name, token],
  queryFn: () => getIntegrationEvents({
    workspace_id: workspaceId,
    integration_name,
    token,
  }),
  select: (data: any) => data?.data,
  ...queryOptions,
});

export const useCurrentWorkspace = ({ workspaceId, ...queryOptions }:
{ workspaceId: string } & UseQueryOptions) => useQuery({
  queryKey: ["workspace", workspaceId],
  queryFn: () => getWorkspace({
    workspace_id: workspaceId,
  }),
  select: (data: any) => data?.data,
  staleTime: 1000 * 60 * 60 * 24,
  ...queryOptions,
});

export const useWorkspacePreferences = ({ workspaceId, ...queryOptions }:
{ workspaceId: string } & UseQueryOptions) => useQuery({
  queryKey: ["workspace_preferences", workspaceId],
  queryFn: () => readWorkspacePreference({
    workspace_id: workspaceId,
  }),
  select: (data: any) => data?.data,
  staleTime: 1000 * 60 * 30,
  ...queryOptions,
});

export const useWorkspaceMetricPreferences = ({ workspaceId, ...queryOptions }:
{ workspaceId: string } & UseQueryOptions) => useQuery({
  queryKey: ["workspace_metric_preferences", workspaceId],
  queryFn: () => readAllMetrics({
    workspace_id: workspaceId,
  }),
  select: (data: any) => data?.data,
  staleTime: 1000 * 60 * 30,
  ...queryOptions,
});

export const useWorkspaceMembers = ({ workspaceId, ...queryOptions }:
{ workspaceId: string } & UseQueryOptions) => useQuery({
  queryKey: ["workspace_members", workspaceId],
  queryFn: () => allWorkspaceMembers({ workspace_id: workspaceId }),
  select: (data: any) => data?.data,
  staleTime: 1000 * 60 * 60 * 24,
  ...queryOptions,
});

export const useViews = ({ workspaceId, ...queryOptions }:
{ workspaceId: string } & UseQueryOptions) => useQuery({
  queryKey: ["views", workspaceId],
  queryFn: () => getViews({
    workspace_identifier: workspaceId,
  }),
  select: (data: any) => data?.data?.views,
  staleTime: 1000 * 60 * 60 * 24,
  ...queryOptions,
});

export const useTraitsAndMetrics = ({ workspaceId, ...queryOptions }:
{ workspaceId: string } & UseQueryOptions) => useQuery({
  queryKey: ["traits_metrics", workspaceId],
  queryFn: () => getWorkspaceTraitsAndMetrics({
    workspaceId,
  }),
  select: (data: any) => {
    if (data) {
      const {
      // eslint-disable-next-line @typescript-eslint/naming-convention
        account_metrics, account_traits, contact_metrics, contact_traits,
      } = data;
      return {
        accountMetrics: {
          list: account_metrics,
          obj: arrayToObject(account_metrics, "name"),
        },
        accountTraits: {
          list: account_traits,
          obj: arrayToObject(account_traits, "name"),
        },
        contactMetrics: {
          list: contact_metrics,
          obj: arrayToObject(contact_metrics, "name"),
        },
        contactTraits: {
          list: contact_traits,
          obj: arrayToObject(contact_traits, "name"),
        },
      };
    }
    return {
      accountMetrics: {
        list: [],
        obj: {},
      },
      accountTraits: {
        list: [],
        obj: {},
      },
      contactMetrics: {
        list: [],
        obj: {},
      },
      contactTraits: {
        list: [],
        obj: {},
      },
    };
  },
  staleTime: 1000 * 60 * 60 * 24,
  ...queryOptions,
});

export const useAccount = ({ workspaceId, accountId, ...queryOptions }:
{ workspaceId: string, accountId: string } & UseQueryOptions) => useQuery({
  queryKey: ["account", workspaceId, accountId],
  queryFn: () => getAccountById(
    { workspace_identifier: workspaceId, external_id: accountId },
  ),
  select: (data: any) => data?.data,
  staleTime: 1000 * 60 * 10,
  ...queryOptions,
});

export const useContact = ({ workspaceId, contactId, ...queryOptions }:
{ workspaceId: string, contactId: string } & UseQueryOptions) => useQuery({
  queryKey: ["contact", workspaceId, contactId],
  queryFn: () => getContactById(
    { workspace_identifier: workspaceId, external_id: contactId },
  ),
  select: (data: any) => data?.data,
  staleTime: 1000 * 60 * 10,
  ...queryOptions,
});

export const useAccountContacts = ({ workspaceId, accountId, ...queryOptions }:
{ workspaceId: string, accountId: string } & UseQueryOptions) => useQuery({
  queryKey: ["account_contacts", workspaceId, accountId],
  queryFn: () => getContactsByAccountId(
    { workspace_identifier: workspaceId, company_id: accountId },
  ),
  select: (data: any) => data?.data,
  staleTime: 1000 * 60 * 10,
  ...queryOptions,
});

export const useAllAutomations = ({ workspaceId, automationType, ...queryOptions }:
{ workspaceId: string, automationType: string } & UseQueryOptions) => useQuery({
  queryKey: ["all_automations", workspaceId, automationType],
  queryFn: () => readAllAutomations({ workspace_id: workspaceId, automation_type: automationType }),
  select: (data: any) => data?.data,
  staleTime: 1000 * 60 * 10,
  ...queryOptions,
});

export const useAllPlaybooks = ({ workspaceId, ...queryOptions }:
{ workspaceId: string } & UseQueryOptions) => useQuery({
  queryKey: ["all_playbooks", workspaceId],
  queryFn: () => readAllPlaybooks({ workspace_id: workspaceId }),
  select: (data: any) => data?.data,
  staleTime: 1000 * 60 * 10,
  ...queryOptions,
});

export const useAutomationLogs = ({
  workspaceId, automationId, page = 0, status, automationType, ...queryOptions
}:
{ workspaceId: string,
  automationType?: string,
  automationId?: string,
  status?:string,
  page?:number } & UseQueryOptions) => useQuery({
  queryKey: ["automation_logs", workspaceId, automationId, automationType, status, page],
  queryFn: () => (readAutomationLogs({
    workspace_id: workspaceId,
    automation_id: automationId || undefined,
    automation_type: automationType,
    status: status || undefined,
    skip: page * 20,
    limit: 20,
  })),
  select: (data: any) => data?.data,
  staleTime: 1000 * 60 * 10,
  ...queryOptions,
});

export const useInbox = ({
  workspaceId,
  automationId,
  timeStart,
  timeEnd,
  playbookId,
  signalStatus,
  notificationType,
  entityRelation,
  owner,
  ...queryOptions
}:
{ workspaceId: string,
  timeStart?: string,
  timeEnd?: string,
  playbookId?:string,
  automationId?: string,
  signalStatus?: "no_status" | "starred" | "dismissed" | "Done",
  notificationType: "inbox" | "notification",
  entityRelation?: "accounts" | "users",
  owner?: string[],
} & UseQueryOptions) => useQuery({
  queryKey: ["inbox",
    workspaceId,
    automationId,
    entityRelation,
    timeStart,
    timeEnd,
    playbookId,
    signalStatus,
    notificationType,
    owner],
  queryFn: () => (readSignalRuns({
    workspace_id: workspaceId,
    entity_relation: entityRelation,
    automation_id: automationId || undefined,
    time_start: timeStart,
    time_end: timeEnd,
    playbook_id: playbookId || undefined,
    signal_status: signalStatus || undefined,
    notification_type: notificationType,
    owner_id: owner || undefined,
  })),
  select: (data: any) => data?.data?.results,
  staleTime: 1000 * 60 * 10,
  retryOnMount: false,
  ...queryOptions,
});

export const useAllIntegrations = ({ workspaceId, ...queryOptions }:
{ workspaceId: string } & UseQueryOptions) => useQuery({
  queryKey: ["integrations", workspaceId],
  queryFn: () => getAllIntegration({ workspace_id: workspaceId }),
  select: (data: any) => data?.data,
  staleTime: 1000 * 60 * 10,
  ...queryOptions,
});

export const useSlackIntegration = ({ workspaceId, ...queryOptions }:
{ workspaceId: string } & UseQueryOptions) => useQuery({
  queryKey: ["slack-integration", workspaceId],
  queryFn: () => getSlackIntegration({ workspace_id: workspaceId }),
  select: (data: any) => data?.data,
  staleTime: 1000 * 60 * 10,
  retry: 0,
  ...queryOptions,
});

export const useSlackUsers = ({ workspaceId, refresh = false, ...queryOptions }:
{ workspaceId: string; refresh?: boolean } & UseQueryOptions) => useQuery({
  queryKey: ["slack-users", workspaceId, refresh],
  queryFn: () => getSlackUsers({ workspace_id: workspaceId, refresh }),
  select: (data: any) => data?.data,
  staleTime: refresh ? 0 : 1000 * 60 * 10,
  ...queryOptions,
});

export const useSlackChannels = ({ workspaceId, refresh = false, ...queryOptions }:
{ workspaceId: string; refresh?: boolean } & UseQueryOptions) => useQuery({
  queryKey: ["slack-channels", workspaceId, refresh],
  queryFn: () => getSlackChannels({ workspace_id: workspaceId, refresh }),
  select: (data: any) => data?.data,
  staleTime: refresh ? 0 : 1000 * 60 * 10,
  ...queryOptions,
});

export const useCreateExport = () => useMutation({
  mutationFn: ({ workspace_id, view_id }: { workspace_id: string; view_id: string }) => 
    createExport({ workspace_id, view_id }),
});

export const useListExports = ({ workspaceId, skip, limit, ...queryOptions }:
{ workspaceId: string; skip?: number; limit?: number } & UseQueryOptions) => useQuery({
  queryKey: ["exports", workspaceId, skip, limit],
  queryFn: () => listExports({
    workspace_id: workspaceId,
    skip,
    limit,
  }),
  select: (data: any) => data?.data as ListExportsResponse,
  staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  ...queryOptions,
});

export const useGetExport = ({ workspaceId, id, ...queryOptions }:
{ workspaceId: string; id: string } & UseQueryOptions) => useQuery({
  queryKey: ["export", workspaceId, id],
  queryFn: () => getExport({
    workspace_id: workspaceId,
    id,
  }),
  select: (data: any) => data?.data as ExportResponse,
  staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  ...queryOptions,
});

export const useDownloadExport = () => useMutation({
  mutationFn: ({ workspace_id, id }: { workspace_id: string; id: string }) => 
    downloadExport({ workspace_id, id }),
});

export const useSignalAutomations = ({
  workspaceId,
  automationId,
  status,
  playbookId,
  ...queryOptions
}:
{ workspaceId: string,
  automationId?: string,
  status?: string,
  playbookId?: string } & UseQueryOptions) => useQuery({
  queryKey: ["signal_automations", workspaceId, automationId, status, playbookId],
  queryFn: () => readSignalAutomations({
    workspace_id: workspaceId,
    automation_id: automationId,
    status,
    playbook_id: playbookId,
  }),
  select: (data: any) => data?.data,
  staleTime: 1000 * 60 * 10,
  ...queryOptions,
});
